<template>
  <div class="flex flex-col">
    <div
      class="navigation-title-box flex flex-col justify-center select-none"
      :class="{
        'navigation-title-box-l': mode === 'light',
        'navigation-title-box-s': mode === 'dark',
        'pointer-events-none': changingElection
      }"
    >
      <div class="w-full flex flex-row h-full justify-center">
        <div
          class="navigation-body h-full relative flex flex-row rounded-md lg:text-chplus-l"
          :class="boxStyling"
          :style="{
            backgroundColor: mode === 'light' ? lightBackgroundColor : undefined
          }"
        >
          <span
            v-if="allowElectionChange"
            @click="selectPreviousElection"
            class="cursor-pointer absolute top-0 w-1/3 left-0 h-full"
          ></span>
          <button
            v-if="allowElectionChange"
            @click="selectPreviousElection"
            class="button-arrow flex-none h-full cursor-pointer"
            style="width: 1rem;"
          >
            <icon-angle
              direction="left"
              :class="{
                'large-arrows': mode === 'light',
                'small-arrows': mode === 'dark'
              }"
            />
          </button>
          <div class="w-full h-full mx-1 lg:mx-2">
            <span
              class="flex flex-row w-full justify-center h-full items-center"
              style="flex: 1 1 1px;"
              ><span
                class="flex-shrink council-text align-middle"
                v-html="councilText"
                style="overflow: hidden; text-overflow: ellipsis;white-space: nowrap;"
              ></span
              ><span class="flex-shrink-0" v-html="seatsText"></span
            ></span>
          </div>
          <span
            v-if="allowElectionChange"
            @click="selectNextElection"
            class="cursor-pointer absolute top-0 w-1/3 right-0 h-full"
          ></span>
          <button
            v-if="allowElectionChange"
            @click="selectNextElection"
            class="button-arrow flex-none h-full cursor-pointer"
            style="width: 1rem;"
          >
            <icon-angle
              direction="right"
              :class="{
                'large-arrows': mode === 'light',
                'small-arrows': mode === 'dark'
              }"
            />
          </button>

          <icon-notification
            v-if="showNotification"
            top="0"
            right="0"
            transform="translate(50%,-50%)"
          />
        </div>
      </div>
    </div>
    <div
      class="text-center text-chplus-s whitespace-no-wrap lg:mt-2"
      v-if="$scopedSlots.info"
    >
      <slot name="info"></slot>
    </div>
  </div>
</template>

<script>
import IconAngle from "../icons/IconAngle";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ElectionProgressStepId } from "@/utils/ElectionProgressStepId";
import IconNotification from "@/components/icons/IconNotification";

export default {
  name: "NavigationTitleBox",
  components: { IconNotification, IconAngle },
  props: {
    lightTheme: {
      type: Boolean,
      default: false
    },
    lightBackgroundColor: {
      type: String,
      default: "rgba(255,255,255,0)"
    },
    mode: {
      type: String,
      default: "dark",
      validator: function(value) {
        // The value must match one of these strings
        return ["light", "dark"].includes(value);
      }
    },
    /**
     * Whether to show elections in the selection that have not been touched yet
     */
    allowNewElections: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    changingElection: false
  }),
  computed: {
    ...mapGetters("user", ["user"]),
    ...mapGetters("election", [
      "election",
      "elections",
      "activeElection",
      "hasElectionProgress"
    ]),
    ...mapGetters("viewState", ["constituencyLevel", "selectedElection"]),
    ...mapGetters("questionnaire", ["shortAnswered"]),
    availableElections() {
      let result = this.elections.map(electionIdRaw => {
        const electionId = parseInt(electionIdRaw);
        const hasSeenElection = !!this.hasElectionProgress(
          ElectionProgressStepId.Match,
          electionId
        );
        return [electionId, hasSeenElection];
      });

      if (!this.allowNewElections) {
        result = result.filter(e => e[1] === true);
        if (result.length === 0 && this.elections.length !== 0) {
          result = [[this.elections[0], true]];
        }
      }
      return result;
    },
    newElections() {
      return this.availableElections.filter(e => e[1] === false);
    },
    boxStyling() {
      return {
        "bg-contrast-light": this.mode === "dark",
        "text-chplus-regular": this.mode === "dark",
        "text-white": true,
        "content-box": this.mode === "dark",
        "faded-white": this.mode === "light",
        "text-chplus-l": this.mode === "light",
        "home-box": this.mode === "light"
      };
    },
    allowElectionChange() {
      return this.availableElections.length > 1 && !this.disabled;
    },
    councilText() {
      const nextElectionYear = new Date(
        this.activeElection?.electionDate
      ).getFullYear();

      let label = this.activeElection?.label;
      if (nextElectionYear < new Date().getFullYear()) {
        label = `${nextElectionYear} ${label}`;
      }

      return this.$t("components.nav.title_box.council", {
        council: label,
        seats: this.activeElection.seats
      });
    },
    seatsText() {
      return this.$t("components.nav.title_box.seats", {
        council: this.activeElection.label,
        seats: this.activeElection.seats
      });
    },
    showNotification() {
      if (this.activeElection == null) {
        return false;
      }
      const newElections = this.newElections.filter(
        e => e[0] !== this.activeElection.id
      );
      return newElections.length > 0;
    },
    index: {
      get() {
        if (this.selectedElection[this.constituencyLevel] == null) {
          return 0;
        }
        return this.selectedElection[this.constituencyLevel];
      },
      set(value) {
        this.setSelectedElection({ key: this.constituencyLevel, value: value });
      }
    }
  },
  watch: {
    availableElections() {
      if (!this.availableElections) {
        return;
      }

      if (this.availableElections.length === 0) {
        this.index = -1;
      } else {
        this.index = Math.max(
          Math.min(this.availableElections.length - 1, this.index),
          0
        );
        this.setElection(this.availableElections[this.index][0]);
      }
    }
  },
  methods: {
    ...mapActions(["store"]),
    ...mapActions("election", ["setActiveElection"]),
    ...mapActions("candidate", ["fetchCandidates", "fetchMatches"]),
    ...mapActions("list", ["fetchLists", "fetchChoice"]),
    ...mapMutations("list", { resetList: "reset" }),
    ...mapMutations("candidate", { resetCandidate: "reset" }),
    ...mapMutations("viewState", {
      resetViewState: "reset",
      setSelectedElection: "setSelectedElection"
    }),
    async selectPreviousElection() {
      if (this.index > 0) {
        this.index--;
      } else {
        const electionIds = this.availableElections;
        this.index = electionIds.length - 1;
      }

      await this.setElection(this.availableElections[this.index][0]);
    },
    async selectNextElection() {
      const elections = this.availableElections;
      if (this.index + 1 < elections.length) {
        this.index++;
      } else {
        this.index = 0;
      }
      await this.setElection(elections[this.index][0]);
    },
    async setElection(electionId) {
      if (this.changingElection) {
        return;
      }
      this.changingElection = true;
      this.$emit("beforeelectionchange");
      await this.setActiveElection({ electionId });
      this.resetViewState();
      this.resetCandidate();
      this.resetList();
      await Promise.all([
        this.fetchLists(),
        await this.fetchCandidates(),
        this.fetchMatches(),
        this.fetchChoice()
      ]);
      this.$emit("electionchange");
      this.changingElection = false;
    }
  }
};
</script>

<style scoped>
.navigation-title-box-s {
  margin-left: auto;
  margin-right: auto;
  width: 19.5rem;
  max-width: 68%;
}

.navigation-title-box-l {
  margin-left: auto;
  margin-right: auto;
  max-width: 93%;
}

.navigation-title-box-l .navigation-body {
  min-width: 20rem;
}

.button-arrow {
  position: relative;
}

.button-arrow .svg-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.button-arrow .svg-icon.left {
  transform: translate(-50%, -50%) rotate(180deg);
}

.content-box {
  height: 1.8rem;
  padding: 0 0.2rem;
  box-sizing: border-box;
}

.home-box {
  height: 2.52rem;
  padding: 0 0.5rem;
  box-sizing: border-box;
}

@media screen and (min-width: 768px) {
  .navigation-title-box-s {
    min-width: 22rem;
  }

  .navigation-title-box-l {
    min-width: 23rem;
  }
}

@media screen and (min-height: 1024px) {
  .content-box {
    height: 1.9375rem;
    padding: 0 0.2rem;
  }

  .home-box {
    height: 2.88rem;
    padding: 0 0.5rem;
    box-sizing: border-box;
  }
}

@media (min-width: 1024px) {
  .navigation-title-box {
    height: 2.52rem;
  }
  .content-box {
    height: 100%;
    padding: 0 0.6rem;
  }
}
</style>
